import React, { useState, useEffect, ChangeEvent } from 'react';
import { useAPI } from 'utils/api';
import { Helmet } from 'react-helmet-async';
import { Checkbox } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'context/global/GlobalContext';
import { showToast } from 'components/atomic/Toast/Toast';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import Input from 'components/atomic/Input';
import Button from 'components/atomic/Button';
import Scrollbar from 'utils/Scrollbar';
import PageWrapper from 'components/layout/PageWrapper';
import validateNewDtcVisitor from 'utils/validateNewDtcVisitor';

const getInitialOwnerName = (firstName: string, email: string): string => {
  if (firstName) return firstName;
  if (email) return maskEmail(email);
  return '';
};

const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return '';
  const [localPart, domain] = email.split('@');
  if (localPart.length <= 2) return email;
  const maskedLocal =
    localPart.substring(0, 2) + '*'.repeat(localPart.length - 2);
  return `${maskedLocal}@${domain}`;
};

const MarkAsLost: React.FC = () => {
  const { user, logEvent, productDetails, reFetchProduct, personalDetails } =
    useGlobal();
  const [ownerName, setOwnerName] = useState<string>(
    getInitialOwnerName(
      personalDetails?.profile?.firstName || '',
      user?.email || ''
    )
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [itemStatus, setItemStatus] = useState<
    'item-lost' | 'item-stolen' | ''
  >('');
  const { brandTheme } = useThemeContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'lostAndFound',
  });

  useEffect(() => {
    setItemStatus(
      (productDetails?.unitTagDetails?.markedAsLost?.itemStatus as
        | 'item-lost'
        | 'item-stolen'
        | '') || ''
    );
  }, [productDetails?.unitTagDetails?.markedAsLost]);

  const endpoint = `lostAndFound/mark-as-lost?ownerName=${encodeURIComponent(
    ownerName
  )}&itemStatus=${encodeURIComponent(itemStatus)}&tagId=${
    productDetails?.tag?.id
  }`;

  const [sendMessage] = useAPI({
    endpoint,
    method: 'POST',
    onSuccess: (response) => {
      if (response.success) {
        showToast({
          message:
            response.message ||
            `Your item has been successfully marked as ${
              itemStatus === 'item-lost'
                ? 'lost'
                : itemStatus === 'item-stolen'
                ? 'stolen'
                : ''
            }`,
          type: 'success',
        });
        if (itemStatus !== '') {
          logEvent({
            eventType: 'ENGAGEMENTS',
            event: 'ITEM_LOST',
            customerNewDtcVisitor: validateNewDtcVisitor(
              productDetails?.brand?.website,
              productDetails?.brand?.shopifyStoreName
            ),
            data: {
              markedAs: itemStatus === 'item-lost' ? 'lost' : 'stolen',
              ownerName,
            },
          });
        }
        setLoading(false);
        reFetchProduct();
      } else {
        showToast({
          message:
            response.error || response.message || t('errorSendingMessage'),
          type: 'error',
        });
        setLoading(false);
      }
    },
    onError: () => {
      showToast({
        message: t('errorSendingMessage'),
        type: 'error',
      });
      setLoading(false);
    },
  });

  return (
    <PageWrapper
      pageTitle={t('markAsLostTitle')}
      allowGoBack={productDetails ? true : false}
    >
      <Helmet>{t('pageTitle')}</Helmet>
      <Scrollbar style={{ background: '#FFFFFF' }}>
        <form
          className='relative w-full flex flex-col px-5 pt-4 pb-8 gap-4 overflow-x-hidden overflow-y-auto'
          onSubmit={(event) => {
            event.preventDefault();
            setLoading(true);
            sendMessage(undefined, null, true);
          }}
        >
          <Input
            type='text'
            name='owner-name'
            value={ownerName}
            placeholder={t('ownerNameInputPlaceholder')}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setOwnerName(event.target.value)
            }
          />
          <div
            key='item-lost'
            className='w-full flex items-start justify-start px-3'
          >
            <Checkbox
              id='item-lost'
              value='item-lost'
              name='item-status'
              className='w-5 h-5'
              checked={itemStatus === 'item-lost'}
              onChange={() =>
                itemStatus === 'item-lost'
                  ? setItemStatus('')
                  : setItemStatus('item-lost')
              }
              style={{
                color: itemStatus === 'item-lost' ? brandTheme : '#000000',
              }}
            />
            <label
              className='ml-3 text-sm font-semibold leading-5 text-dark'
              htmlFor='item-lost'
            >
              {t('itemLostCheckbox')}
            </label>
          </div>
          <div
            key='item-stolen'
            className='w-full flex items-start justify-start px-3'
          >
            <Checkbox
              name='item-status'
              id='item-stolen'
              value='item-stolen'
              className='w-5 h-5'
              checked={itemStatus === 'item-stolen'}
              onChange={() =>
                itemStatus === 'item-stolen'
                  ? setItemStatus('')
                  : setItemStatus('item-stolen')
              }
              style={{
                color: itemStatus === 'item-stolen' ? brandTheme : '#000000',
              }}
            />
            <label
              className='ml-3 text-sm font-semibold leading-5 text-dark'
              htmlFor='item-stolen'
            >
              {t('itemStolenCheckbox')}
            </label>
          </div>
          <Button
            title={t('saveButton')}
            type='submit'
            variant='dark'
            className='w-full mt-2'
            disabled={!ownerName.trim()}
            loading={loading}
          />
        </form>
      </Scrollbar>
    </PageWrapper>
  );
};

export default MarkAsLost;
